import { ColDef } from "ag-grid-community";
import { Buttons } from "../../../components/atoms/Buttons";
import { InputSelect } from "../../../components/atoms/Inputs/InputSelect";
import { Grid } from "../../../components/molecules/Grid";
import { Legend } from "../../../components/molecules/Legends";
import { SearchBar } from "../../../components/molecules/SearchBar";
import { PageContent } from "../../../components/organisms/Contents/PageContent";
import { FormEvent, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { currentShipState } from "../../../common/states/ship";
import { useMutation, useQuery } from "@tanstack/react-query";
import useStatusSearchInputData from "../../../common/hooks/useStatusSearchInputData";
import { useForm } from "react-hook-form";
import { Status } from "../../../common/types/status";
import { request } from "../../../common/api";
import { GET_STATUS } from "../../../common/query-keys";
import { showAlert } from "../../../components/containers/Alert";
import { ReactComponent as IconComment } from "../../../styles/assets/svg/icon-comment.svg";
import { LedLegendItem } from "../../../components/atoms/LegendItem/LedLegendItem";
import * as XLSX from "xlsx";
import { InputDateHour } from "../../../components/atoms/Inputs/InputDateHour";
import ModalComment from "../../../components/molecules/Modals/ModalComment";
import { validateDateRange } from "../../../common/functions/validateFunctions";
import useDiagnosisContextMenu from "../../../common/hooks/useDiagnosisContextMenu";
import { ModalLoading } from "../../../components/molecules/Modals/ModalLoading";
import { startDateHourHandler } from "../../../common/util/setEndDateAuto";
import { beforeWeekTime } from "../../../common/util/beforeWeek";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as IconFind } from "../../../styles/assets/svg/icon_find.svg";

export interface StatusSearchInputs {
  steShiptypeName?: string;
  steShipName?: string;
  steShiptypeNo?: string;
  steShipNo?: string;
  steMcyName?: string;
  ssrMachine?: string;
  steLowMcyName?: string;
  steSensorCode?: string;
  steSensorName?: string;
  steAlarmType?: string;
  ssrSignal?: string;
  startDate?: string;
  endDate?: string;
}

export const StatusPage = () => {
  const currentShip = useRecoilValue(currentShipState);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const searchDefaultValues = {
    steShiptypeNo: currentShip.shipTypeNo, // 해상에서 추가
    steShiptypeName: currentShip.shipTypeName,
    steShipNo: currentShip.shipNo, // 해상에서 추가
    steShipName: currentShip.shipName,
    steMcyName: "",
    ssrMachine: "",
    steLowMcyName: "",
    steSensorName: "",
    steAlarmType: "",
    startDate: dayjs().subtract(7, "days").format("YYYY-MM-DD 00:00:00"),
    endDate: dayjs().format("YYYY-MM-DD 23:59:59"),
  };

  const {
    search,
    setSearch,
    machinerySelectOptions,
    lowMachinerySelectOptions,
    sensorSelectOptions,
    handleMcySelectChange,
    handleLowMcySelectChange,
    handleSensorSelectChange,
    handleAlarmSelectChange,
    handleValueChange,
    setSearchData,
  } = useStatusSearchInputData(searchDefaultValues);

  const { contextMenu, handleContextMenu, handleOpenInNewTab } =
    useDiagnosisContextMenu();

  const [selectedKey, setSelectedKey] = useState<number>(0);
  const [searchInputs, setSearchInputs] = useState<StatusSearchInputs>();
  const [isModal, setIsModal] = useState<boolean>(false);

  const { register, setValue, control, handleSubmit } = useForm();

  /** 상태 진단 알람 데이터 조회 */
  const {
    data: statusData,
    refetch: statusDataRefetch,
    isFetching: isStatusLoading,
  } = useQuery({
    queryKey: [GET_STATUS, searchInputs],
    queryFn: async () => {
      return await request<Status[]>({
        method: "GET",
        url: "/state-alarm",
        params: searchInputs,
      });
    },
    enabled: false,
  });

  /** 검색 조건 검색 버튼 */
  const onSearchHandler = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { steMcyName, steLowMcyName, steSensorName, startDate, endDate } =
      search;

    const isValid = validateDateRange(startDate, endDate, 7);

    if (!isValid) {
      return;
    }

    if (!steMcyName) {
      return showAlert("장비명을 선택해주세요.");
    } else if (!steLowMcyName) {
      return showAlert("계통을 선택해주세요.");
    } else if (!steSensorName) {
      return showAlert("센서명을 선택해주세요.");
    }

    setSearchInputs(search);
  };

  useEffect(() => {
    if (searchInputs) {
      statusDataRefetch();
    }
  }, [searchInputs]);

  useEffect(() => {
    if (location.state && location.state.search) {
      setSearch(location.state.search);
      setSearchInputs(location.state.search);
    }

    const handleBeforeUnload = () => {
      navigate("/support/alarm/status", {
        state: { search: searchDefaultValues },
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [location.state]);

  /** 코멘트 작성/수정 API */
  const { mutateAsync: postStateAlarmComment } = useMutation({
    mutationFn: (data: any) =>
      request({
        method: "POST",
        url: "/state-alarm/comment",
        data: data,
      }),
    onSuccess: (res: any) => {
      showAlert("코멘트를 작성하였습니다.");
      setIsModal(false);
      statusDataRefetch();
    },
  });

  /** 그리드 코멘트 버튼 */
  const commentButtonRender = (e: any) => {
    return (
      <div
        style={{ width: "100%", display: "flex", justifyContent: "center" }}
        onClick={() => {
          setSelectedKey(e.data.steId);
          setValue("steComment", e.data.steComment);
          setIsModal(true);
        }}
      >
        {e.data.steCommentState === 0 ? (
          <IconComment fill="black" fillOpacity={0.2} />
        ) : (
          <IconComment fill="#4B45E7" />
        )}
      </div>
    );
  };

  // /** 그리드 보고서 버튼 */
  // const reportButtonRender = (e: any) => {
  //   return (
  //     <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
  //       {e.data.steReportState === 0 ? (
  //         <IconEmptyReport />
  //       ) : e.data.steReportState === 1 ? (
  //         <IconEditReport />
  //       ) : (
  //         <IconCheckReport />
  //       )}
  //     </div>
  //   );
  // };

  const legendItemRenderer = (params: any) => {
    const alarmType = params.data.steAlarmType;
    switch (alarmType) {
      case 1:
        return (
          <LedLegendItem
            ledColor="yellow"
            title=""
            onContextMenu={(e) =>
              handleContextMenu(e, {
                ste_shiptype_no: params.data.steShiptypeNo,
                ste_ship_no: params.data.steShipNo,
                mcyNo: params.data.steMcyNo,
                ste_mcy_name: params.data.steMcyName,
                ste_low_mcy_name: params.data.steLowMcyName,
                ste_sensor_code: params.data.steSensorCode,
                ste_alarm_time: params.data.steAlarmTime,
              })
            }
          />
        );
      case 2:
        return (
          <LedLegendItem
            ledColor="red"
            title=""
            onContextMenu={(e) =>
              // 기존의 handleContextMenu 함수에 data를 넘겨주는 방식으로 변경 처음 적용한 데이터들이 스네이크 케이스로 되어있어서 한번더 수정
              handleContextMenu(e, {
                ste_shiptype_no: params.data.steShiptypeNo,
                ste_ship_no: params.data.steShipNo,
                mcyNo: params.data.steMcyNo,
                ste_mcy_name: params.data.steMcyName,
                ste_low_mcy_name: params.data.steLowMcyName,
                ste_sensor_code: params.data.steSensorCode,
                ste_alarm_time: params.data.steAlarmTime,
              })
            }
          />
        );
      default:
        // return;
        return <LedLegendItem ledColor="green" title="" />;
    }
  };

  /** Excel 다운로드 */
  const exportToCSV = (data: Status[]) => {
    // 각 열의 Header Name 설정
    const formattedData = data.map((item, idx) => {
      return {
        No: idx + 1,
        알람발생시간: dayjs(item.steAlarmTime).format("YYYY.MM.DD_HH:mm:ss"),
        알람레벨:
          item.steAlarmType === 0
            ? "Normal"
            : item.steAlarmType === 1
            ? "Warning"
            : "Critical",
        // 함형: item.steShiptypeName,
        // 함정: item.steShipName,
        장비: item.steMcyName,
        계통: item.steMcyName,
        센서: item.steSensorName,
        센서코드: item.steSensorCode,
        // 알림메시지: item.steAlarmMsg,
        // 알림값: item.steAlarmValue,
        // 기준값: item.steAlarmBase,
        // 편차: item.steAlarmDev,
      };
    });

    // 데이터를 워크시트로 변환
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // 워크시트를 CSV로 변환
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);

    // CSV 문자열에서 Blob 생성 및 다운로드 트리거
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // 파일 이름 설정
    let fileName = `${data[0].steShiptypeName}_상태진단.csv`;

    if (searchInputs?.steLowMcyName) {
      fileName = `${data[0].steShiptypeName}_${data[0].steShipName}_${searchInputs?.steMcyName}_${searchInputs?.steLowMcyName}_상태진단.csv`;
    } else if (searchInputs?.steMcyName) {
      fileName = `${data[0].steShiptypeName}_${data[0].steShipName}_${searchInputs?.steMcyName}_상태진단.csv`;
    } else if (searchInputs?.steShipName) {
      fileName = `${data[0].steShiptypeName}_${data[0].steShipName}_상태진단.csv`;
    }

    // 다운로드 트리거
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsLoading(false);
  };

  const handleDownloadClick = () => {
    setIsLoading(true);
    try {
      if (statusData && statusData.length > 0) {
        exportToCSV(statusData);
      } else {
        setIsLoading(false);
        showAlert("조회된 데이터 결과가 없습니다.");
      }
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "No",
      valueGetter: "node.rowIndex + 1",
      width: 50,
    },
    {
      headerName: "알람발생시간",
      field: "steAlarmTime",
      flex: 1,
      valueGetter: (params: any) =>
        dayjs(params.data.steAlarmTime).format("YYYY.MM.DD_HH:mm:ss"),
    },
    {
      headerName: "알람 레벨",
      field: "steAlarmType",
      width: 120,
      cellRenderer: legendItemRenderer,
    },
    // {
    //   headerName: "알림값",
    //   field: "steAlarmValue",
    //   width: 80,
    // },
    // {
    //   headerName: "함형",
    //   field: "steShiptypeName",
    //   width: 120,
    // },
    // {
    //   headerName: "함정",
    //   field: "steShipName",
    //   width: 120,
    // },
    {
      headerName: "장비",
      field: "steMcyName",
      flex: 0.5,
    },
    {
      headerName: "계통",
      field: "steLowMcyName",
      flex: 0.5,
    },
    {
      headerName: "센서코드",
      field: "steSensorCode",
      flex: 1,
    },
    {
      headerName: "센서명",
      field: "steSensorName",
      flex: 1,
    },
    // {
    //   headerName: "알람 메시지",
    //   field: "steAlarmMsg",
    //   width: 200,
    // },

    // {
    //   headerName: "기준값",
    //   field: "steAlarmBase",
    //   width: 80,
    // },
    // {
    //   headerName: "편차",
    //   field: "steAlarmDev",
    //   width: 80,
    // },
    // {
    //   headerName: "코멘트",
    //   field: "steComment",
    //   width: 70,
    //   cellRenderer: commentButtonRender,
    // },
    // {
    //   headerName: "보고서",
    //   field: "steReportState",
    //   width: 70,
    //   cellRenderer: reportButtonRender,
    // },
  ];

  const onSubmit = async (data: any) => {
    try {
      await postStateAlarmComment({ steId: selectedKey, ...data });
    } catch (error) {
      console.error("POST 요청 실패:", error);
    }
  };

  return (
    <PageContent
      depth01Title="정비 지원"
      depth02Title="함정 알람 관리"
      depth03Title="상태진단 알람"
    >
      <>
        <SearchBar
          onSearch={onSearchHandler}
          searchAlignStart={true}
          searchTitle=" "
          searchInput={
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                gap: "12px",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "row", gap: "4px" }}
              >
                <InputSelect
                  name="steMcyName"
                  size="md"
                  placeholder="장비 선택"
                  options={machinerySelectOptions}
                  width={120}
                  onChange={handleMcySelectChange}
                  // value={search.ssrMachine}
                  value={search.steMcyName}
                />
                <InputSelect
                  name="steLowMcyName"
                  size="md"
                  placeholder="계통 선택"
                  options={lowMachinerySelectOptions}
                  width={120}
                  onChange={handleLowMcySelectChange}
                  disabled={!search.steMcyName}
                  value={search.steLowMcyName}
                />
                <InputSelect
                  size="md"
                  width={120}
                  name="ssrSignal"
                  options={[
                    { label: "디지털", value: "D" },
                    { label: "아날로그", value: "A" },
                  ]}
                  placeholder="센서 신호 선택"
                  onChange={(e) => setSearchData(e.target.name, e.target.value)}
                  value={search.ssrSignal}
                  disabled={!search.steLowMcyName}
                />
                <InputSelect
                  name="steSensorName"
                  size="md"
                  placeholder="센서 선택"
                  options={sensorSelectOptions}
                  width={220}
                  onChange={handleSensorSelectChange}
                  disabled={!search.steLowMcyName}
                  // value={search.steSensorName}
                  value={search.steSensorCode}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <InputSelect
                  name="steAlarmType"
                  size="md"
                  options={[
                    { label: "Normal", value: "0" },
                    { label: "Warning", value: "1" },
                    { label: "Critical", value: "2" },
                  ]}
                  placeholder="레벨 선택"
                  width={120}
                  onChange={handleAlarmSelectChange}
                  value={search.steAlarmType}
                />
                <InputDateHour
                  startName="startDate"
                  size="md"
                  endDate={true}
                  width={200}
                  defaultValue={search.startDate}
                  value={search.startDate}
                  onChange={(e) => startDateHourHandler(e, setSearchData, 7)}
                />
                ~
                <InputDateHour
                  startName="endDate"
                  size="md"
                  endDate={true}
                  width={200}
                  defaultValue={search.endDate}
                  value={search.endDate}
                  onChange={handleValueChange}
                />
                <Buttons
                  type="submit"
                  size="md"
                  layout="selectCondition"
                  label="전1주"
                  onClick={() => beforeWeekTime(search, setSearchData, 7)}
                />
                <Buttons type="submit" size="md" layout="find">
                  <IconFind />
                </Buttons>
              </div>
            </div>
          }
          titleButtons={
            <Buttons
              type="button"
              size="md"
              layout="primary"
              label="csv 다운로드"
              onClick={handleDownloadClick}
            />
          }
        />
        <Legend title="알람 레벨" type="uptime">
          <>
            <LedLegendItem ledColor="green" title="Normal" />
            <LedLegendItem ledColor="red" title="Critical" />
            <LedLegendItem ledColor="yellow" title="Warning" />
          </>
        </Legend>
        <Grid
          rowData={statusData || []}
          columnDefs={columnDefs}
          loading={isStatusLoading}
          // defaultPageSize={15}
          paging={false}
          pagingShow={false}
        />
        {isModal && (
          <ModalComment
            formId="statusCommentSave"
            register={register}
            setModalShow={() => setIsModal(false)}
            control={control}
            onSubmit={handleSubmit(onSubmit)}
          />
        )}
        {isLoading && <ModalLoading modalText="Excel 파일을 생성 중입니다." />}
      </>
    </PageContent>
  );
};
